import Button from "react-bootstrap/esm/Button";
import React, { FormEvent, useEffect, useRef, useState } from "react";
import Col from "react-bootstrap/esm/Col";
import Container from "react-bootstrap/esm/Container";
import Row from "react-bootstrap/esm/Row";
import Form from "react-bootstrap/Form";
import ReportsTable from "../../components/tables/reportsTable";
import { ColumnDef } from "@tanstack/react-table";
import { fuzzySort } from "../../components/tables/fuzzySort";
import { CatchReportPeriod } from "../../models/catchReportPeriod";
import { getFormattedDateMMDDYYYY } from "../../helpers/getFormattedDateMMDDYYYY";
import { CatchReport } from "../../models/catchReport";
import OverlayTrigger from "react-bootstrap/esm/OverlayTrigger";
import Tooltip from "react-bootstrap/esm/Tooltip";
import { FaExternalLinkAlt, FaEye, FaFileDownload } from "react-icons/fa";
import { Link } from "react-router-dom";
import { getOrganizationID, getRoles } from "../../helpers/authHelper";
import Spinner from "react-bootstrap/Spinner";
import SelectOrganization from "../../components/formControls/selectOrganization";
import { TripType } from "../../models/tripType";
import { lookupTripTypes } from "../../api/lookupTripTypes";
import { getCatchReportPeriodsAdmin } from "../../api/getCatchReportPeriodsAdmin";
import { getFormattedDateYYYYMMDD } from "../../helpers/getFormattedDateYYYYMMDD";
import {
  Unsubmitted,
  getUnsubmittedReport,
} from "../../api/getUnsubmittedReport";
import Chart from "chart.js/auto";
import { downloadJSONAsCSV } from "../../helpers/downloadJSONAsCSV";
import UnsubmittedChart from "../../components/charts/UnsubmittedChart";

export default function UnsubmittedReport() {
  // data
  const [reportData, setReportData] = useState<any[]>([]);

  // State management
  const [isDownloadingCSV, setIsDownloadingCSV] = useState(false);
  const [isLoadingReport, setIsLoadingReport] = useState(false);

  // Filters
  const [beginDate, setBeginDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedCatchReportIndex, setSelectedCatchReportIndex] = useState(0);
  const [selectedOrg, setSelectedOrg] = useState(
    getRoles().includes("CORA_Admin") ? -1 : getOrganizationID()
  );
  const [selectedTripTypeID, setSelectedTripTypeID] = useState(-1);
  const [excludeCrewSubmitted, setExcludeCrewSubmitted] = useState(true);

  // Lookups
  const [catchReportPeriods, setCatchReportPeriods] = useState<
    CatchReportPeriod[]
  >([]);
  const [tripTypesList, setTripTypesList] = useState<TripType[]>([]);

  useEffect(() => {
    if (getRoles().includes("CORA_Admin")) {
      const fetch = async () => {};
      fetch();
    } else {
      const fetch = async () => {};
      fetch();
    }
  }, [selectedOrg]);

  // On load - get lookups
  useEffect(() => {
    lookupTripTypes().then((res) => {
      if (res) setTripTypesList(res);
    });
  }, []);

  useEffect(() => {
    const fetch = async () => {
      const resCatchReportPeriods = await getCatchReportPeriodsAdmin({
        organizationID: selectedOrg,
        includeNext: true,
        excludeSubmitted: false,
      });

      if (resCatchReportPeriods)
        setCatchReportPeriods([...resCatchReportPeriods]);
    };
    fetch();
  }, [selectedOrg]);

  const handleSelectChange = async (event: any) => {
    const selectedOption = parseInt(event.target.value);
    setSelectedCatchReportIndex(selectedOption);
    setBeginDate(
      getFormattedDateYYYYMMDD(catchReportPeriods[selectedOption].beginDate)
    );
    setEndDate(
      getFormattedDateYYYYMMDD(catchReportPeriods[selectedOption].endDate)
    );
  };

  // When filter review changes, fetch the new trips and update the table
  useEffect(() => {
    const params: Unsubmitted = {
      organizationID: selectedOrg,
      beginDate: beginDate,
      endDate: endDate,
      tripTypeID: selectedTripTypeID,
      excludeCrewSubmitted: excludeCrewSubmitted,
    };

    getReport(params);
  }, [
    selectedOrg,
    beginDate,
    endDate,
    selectedTripTypeID,
    excludeCrewSubmitted,
  ]);

  const getReport = async (params: Unsubmitted) => {
    setIsLoadingReport(true);
    getUnsubmittedReport(params).then((res) => {
      setReportData(res);
      setIsLoadingReport(false);
    });
  };

  const downloadReportAsCSV = async () => {
    if (reportData.length === 0) return;
    setIsDownloadingCSV(true);
    downloadJSONAsCSV(reportData, "Unsubmitted Users.csv");
    setIsDownloadingCSV(false);
  };

  const columns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      {
        accessorFn: (row: any) => {
          return { Code: row.OrganizationCode, License: row.CommercialLicense };
        },
        id: "organization",
        header: "Tribe",
        cell: (info) => {
          return <span title={`License: ${info.getValue().License}`}>{info.getValue().Code}</span>;
        },
        footer: (props) => props.column.id,
        filterFn: "fuzzy",
        sortingFn: (rowA, rowB, columnId) => {
          if (rowA.original.OrganizationCode < rowB.original.OrganizationCode) return 1;
          if (rowA.original.OrganizationCode > rowB.original.OrganizationCode) return -1;
          return 0;
        },
      },
      {
        accessorFn: (row: any) => {
          return { DisplayName: row.DisplayName, UserName: row.UserName, UserID: row.UserID };
        },
        id: "fisher",
        header: "Fisher",
        cell: (info) => {
          return <strong><a href={`/fisher/${info.getValue().UserID}`} title={`Username: ${info.getValue().UserName}`} target="_blank" rel="noreferrer">{info.getValue().DisplayName}</a></strong>;
        },
        footer: (props) => props.column.id,
        filterFn: "fuzzy",
        sortingFn: (rowA, rowB, columnId) => {
          return rowB.original.DisplayName.localeCompare(rowA.original.DisplayName, undefined, { sensitivity: 'base' })
        },
      },
      {
        accessorFn: (row) => {
          const phone = row["Phone"];
          return phone && typeof phone === "string" ? phone : "";
        },
        id: "phone",
        header: "Phone",
        cell: (info) => {
          return  <a href={`tel:${info.getValue()}`} title="Click to dial">{info.getValue()}</a>;
        },
        footer: (props) => props.column.id,
        filterFn: "fuzzy",
        sortingFn: fuzzySort,
      },
      {
        accessorFn: (row) => row["Email"],
        id: "email",
        header: "Email",
        cell: (info) => {
          return <a href={`mailto:${info.getValue()}`} title="Click to email">{info.getValue()}</a>;
        },
        footer: (props) => props.column.id,
        filterFn: "fuzzy",
        sortingFn: fuzzySort,
      },
      {
        accessorFn: (row: any) => {
          return { LoginDate: row.LastLogin, ActiveDate: row.CommercialActiveDate };
        },
        id: "lastLogin",
        header: "Last Login",
        cell: (info) => {
          return <span title={`License Activated: ${info.getValue().ActiveDate}`}>{info.getValue().LoginDate}</span>;
        },
        footer: (props) => props.column.id,
        filterFn: "fuzzy",
        sortingFn: (rowA, rowB, columnId) => {
          return new Date(rowA.original.LastLoginDate).getTime() - new Date(rowB.original.LastLoginDate).getTime()
        },
      },
      {
        accessorFn: (row: any) => {
          return { Total: row.CommercialTripCount, Complete: row.CommercialTripCount_Complete };
        },
        id: "commercialTrips",
        header: "Trips Entered",
        cell: (info) => {
          return <span title={`Complete: ${info.getValue().Complete}/${info.getValue().Total}`}>{info.getValue().Total}</span>;
        },
        footer: (props) => props.column.id,
        filterFn: "fuzzy",
        sortingFn: (rowA, rowB, columnId) => {
          return rowA.original.CommercialTripCount - rowB.original.CommercialTripCount
        },
      },
    ],
    [reportData]
  );

  return (
    <div className="py-5">
      <Container fluid>
        <h1 className="text-primary mb-5 display-4 fw-bold">
          Catch Reports Due ({reportData.length})
        </h1>

        <div className="form-card">
          <Row className="gx-3 mb-3">
            {getRoles().includes("CORA_Admin") && (
              <Col xs="auto" lg={4}>
                <SelectOrganization
                  allTribes
                  returnAllOrgs
                  includeCORA={false}
                  value={selectedOrg}
                  onChange={(e: any) => setSelectedOrg(e.target.value)}
                />
              </Col>
            )}
            <Col xs="auto">
              <Form.Group className="mb-3 w-auto ">
                <Form.Label>Reporting Period</Form.Label>
                <Form.Select
                  onChange={handleSelectChange}
                  aria-label="Reporting Period"
                  value={selectedCatchReportIndex}
                >
                  {catchReportPeriods.map((period: CatchReportPeriod, i) => (
                    <option key={i} value={i}>
                      {getFormattedDateMMDDYYYY(period.beginDate)} -{" "}
                      {getFormattedDateMMDDYYYY(period.endDate)}
                    </option>
                  ))}
                  {catchReportPeriods.length > 0 && (
                    <option value={-1}>---</option>
                  )}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs="auto">
              <Form.Label>Type</Form.Label>
              <Form.Select
                aria-label="Select Type"
                value={selectedTripTypeID}
                onChange={(e) =>
                  setSelectedTripTypeID(parseInt(e.target.value))
                }
              >
                <option value={-1}>All Types</option>
                {Array.isArray(tripTypesList) &&
                  tripTypesList.map((type: TripType) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
              </Form.Select>
            </Col>
            {/*<Col xs="auto" className="mt-4">
              <Form.Check
                type="checkbox"
                name="excludeCrewSubmitted"
                id="excludeCrewSubmitted"
                label="Exclude Crew Submitted"
                checked={excludeCrewSubmitted}
                onChange={() => setExcludeCrewSubmitted(!excludeCrewSubmitted)}
              ></Form.Check>
            </Col>*/}
            <Col xs="auto" className="pt-4">
              <Button variant="secondary" onClick={downloadReportAsCSV}>
                {isDownloadingCSV ? (
                  <Spinner
                    animation="border"
                    variant="light"
                    size="sm"
                    className="me-2"
                  />
                ) : (
                  <FaFileDownload className="mt-n1 me-2" />
                )}
                Download List as CSV
              </Button>
            </Col>
          </Row>
        </div>

        {isLoadingReport ? (
          <div className="d-flex justify-content-center align-items-center mb-5">
            <Spinner animation="border" variant="primary" className="ms-2" />
            <span className="h3 ms-3 mb-0 text-primary">
              Loading Unsubmitted Report...
            </span>
          </div>
        ) : (
          <>
            {selectedOrg < 0 && (
              <UnsubmittedChart
                data={reportData}
              />
            )}
            <ReportsTable
              className="mb-5"
              columns={columns}
              catchReports={reportData}
            />
          </>
        )}
      </Container>
    </div>
  );
}
